import React, { useState, useEffect } from "react";
import {
  convertToYYYYMMDD,
  formatDateToDDMMYYYY,
  getMonthPeriodFromDate,
  getCurrentMonthPeriod,
} from "utils/dateUtils";
import {
  Grid,
  TextField,
  useTheme,
  Button,
  Typography,
  IconButton,
  Box,
  InputBase,
} from "@mui/material";
import { useSelector } from "react-redux";
import api from "services/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Search, Visibility, VisibilityOff, DownloadOutlined, } from "@mui/icons-material";
import Header from "components/Header";
import { DataGrid } from "@mui/x-data-grid";
import { handlePrint } from "gestion/utils/printReport";


const Alertes = () => {
  const theme = useTheme();
  const mode = useSelector((state) => state.global.mode);

  // States for your alertes
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredAlertes, setFilteredAlertes] = useState([]);
  const [showAlertes, setShowAlertes] = useState(true);

  const defaultPeriod = getCurrentMonthPeriod();

  const [startDate, setStartDate] = useState(defaultPeriod.startDate);
  const [endDate, setEndDate] = useState(defaultPeriod.endDate);

  useEffect(() => {
    fetchAlertes();
  }, [startDate, endDate]);

  useEffect(() => {
    if (Array.isArray(alertes)) {
      setFilteredAlertes(
        alertes.filter((alerte) =>
          alerte.clientName.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    } else {
      setFilteredAlertes([]);
    }
  }, [searchQuery, alertes]);

  const fetchAlertes = async () => {
    setLoading(true);
    try {

      const response = await api.get(
        `/api/alertesNoRecord/consommations?startDate=${startDate}&endDate=${endDate}`
      );

      setAlertes(response.data.clients || []);
    } catch (error) {
      console.error("Error fetching alertes!", error);
      toast.error("Error fetching alertes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleMonthChange = (inputValue) => {
    const ddmmyyyy = formatDateToDDMMYYYY(inputValue);
    const period = getMonthPeriodFromDate(ddmmyyyy);

    setStartDate(period.startDate);
    setEndDate(period.endDate);
  };

  const columnsAlertes = [
    { field: "id", headerName: "ID Client", width: 80 },
    { field: "clientName", headerName: "Nom Client", width: 130 },
    { field: "waterBillRefNumber", headerName: "Numéro Référence Client", width: 160 },
    { field: "village", headerName: "Village", width: 130 },
    { field: "phoneNumber", headerName: "Téléphone", width: 140 },
    { field: "type", headerName: "Type", width: 120 },
    { field: "contrat", headerName: "Contrat", width: 120 },
    { field: "lastConsumptionDate", headerName: "Dernière consommation", width: 180 },
    { field: "lastPaidDate", headerName: "Dernier paiement", width: 160 },
    { field: "lastIndex", headerName: "Dernier index", width: 130 },
    { field: "paymentStatus", headerName: "Statut paiement", width: 150 },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="Clients sans consommation enregistrée"
          subtitle="Clients absents du mois de consommation rattaché à la période opérationnelle"
          sx={{ mb: 1 }}
        />
        <Box display="flex" alignItems="center" mb="20px">
          <InputBase
            placeholder="Rechercher…"
            value={searchQuery}
            onChange={handleSearch}
            sx={{ ml: 1, flex: 1 }}
            inputProps={{ "aria-label": "search" }}
          />
          <IconButton type="submit" sx={{ p: 1 }} aria-label="search">
            <Search />
          </IconButton>
        </Box>
      </Box>
      <Box display="flex" alignItems="center" gap="12px" mt="20px" flexWrap="wrap">
        <Button
          variant="contained"
          color="secondary"
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            padding: "10px 20px",
            marginTop: "4px",
            marginRight: "5px",
          }}
          onClick={() => handlePrint(filteredAlertes)}
        >
          <DownloadOutlined sx={{ mr: "10px" }} />
          Télécharger le rapport des alertes
        </Button>
        <TextField
          id="start-date"
          label="Période de début"
          type="date"
          value={convertToYYYYMMDD(startDate)}
          onChange={(e) => handleMonthChange(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextField
          id="end-date"
          label="Période de fin"
          type="date"
          value={convertToYYYYMMDD(endDate)}
          onChange={(e) => handleMonthChange(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Box>
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Alertes des Consommations
            <IconButton onClick={() => setShowAlertes(!showAlertes)} sx={{ ml: 1 }}>
              {showAlertes ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </Typography>
          {showAlertes && (
            <Box
              mt="40px"
              height="75vh"
              sx={{
                "& .MuiDataGrid-root": {
                  border: "none",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: theme.palette.background.alt,
                  color: theme.palette.secondary[100],
                  borderBottom: "none",
                },
                "& .MuiDataGrid-virtualScroller": {
                  backgroundColor: theme.palette.primary.light,
                },
                "& .MuiDataGrid-footerContainer": {
                  backgroundColor: theme.palette.background.alt,
                  color: theme.palette.secondary[100],
                  borderTop: "none",
                },
                "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                  color: `${theme.palette.secondary[200]} !important`,
                },
              }}
            >
              <DataGrid
                loading={loading}
                rows={filteredAlertes}
                columns={columnsAlertes}
                pageSize={10}
                rowsPerPageOptions={[10]}
                getRowId={(row) => row.id}
                noRowsOverlay={<div>Pas d'alerte disponible</div>}
              />
            </Box>
          )}
        </Grid>
      </Grid>

    </Box>
  );
};

export default Alertes;

